import { useCallback, useEffect } from "react";
import { useImmer } from "use-immer";
import { useDebounce } from "use-debounce";
import { useStockTagQueries } from "./useStockTagQueries";
import { arraySort } from "../../../utils";

export const useStockItemTags = () => {
  const [state, updateState] = useImmer({
    tags: [],
    activeTagId: null,
    isModalOpen: false,
    selectedTag: null,
    tagName: "",
    deleteInfo: { show: false, id: null },
    search: "",
    selectedItem: {
      id: null,
      open: false,
    },
    addItemsDialog: { open: false },
  });

  const resetDeleteInfo = useCallback(() => {
    updateState((draft) => {
      draft.deleteInfo = { show: false, id: null };
    });
  }, [updateState]);

  const [debouncedText] = useDebounce(state.search, 1000);

  const {
    listQuery,
    createTag,
    updateTag,
    deleteTag,
    deleteTagItem,
    addItems,
  } = useStockTagQueries({
    resetDeleteInfo,
    debouncedText,
  });

  useEffect(() => {
    if (listQuery.data) {
      const sortedTags = arraySort(
        listQuery.data.map((tag) => ({
          ...tag,
          subData: arraySort(tag.subData, "name"),
        })),
        "name",
      );
      updateState((draft) => {
        draft.tags = sortedTags;
        if (!draft.activeTagId && listQuery.data.length) {
          draft.activeTagId = sortedTags[0].id;
        }
      });
    }
  }, [listQuery.data, updateState]);

  const handleSelectTag = useCallback(
    (tagId) => {
      updateState((draft) => {
        draft.activeTagId = tagId;
      });
    },
    [updateState],
  );

  const activeTag = state.tags.find((t) => t.id === state.activeTagId) || null;

  const handleOpenModal = useCallback(
    (tag = null) => {
      updateState((draft) => {
        draft.selectedTag = tag;
        draft.tagName = tag ? tag.name : "";
        draft.isModalOpen = true;
      });
    },
    [updateState],
  );

  const handleCloseModal = useCallback(() => {
    updateState((draft) => {
      draft.isModalOpen = false;
      draft.selectedTag = null;
      draft.tagName = "";
    });
  }, [updateState]);

  const handleTagNameChange = useCallback(
    (e) => {
      updateState((draft) => {
        draft.tagName = e.target.value;
      });
    },
    [updateState],
  );

  const handleSubmit = useCallback(() => {
    if (state.selectedTag) {
      // Edit mode
      const payload = { name: state.tagName };
      updateTag.mutate({
        id: state.selectedTag.id,
        payload,
        handleClose: handleCloseModal,
      });
    } else {
      // Create mode
      const payload = { name: state.tagName };
      createTag.mutate({ payload, handleClose: handleCloseModal });
    }
  }, [
    state.selectedTag,
    state.tagName,
    updateTag,
    createTag,
    handleCloseModal,
  ]);

  const handleDelete = useCallback(
    (id) => {
      updateState((draft) => {
        draft.deleteInfo = { show: true, id, type: "tag" };
      });
    },
    [updateState],
  );

  const handleDeleteItem = useCallback(
    (itemId) => {
      updateState((draft) => {
        draft.deleteInfo = { show: true, id: itemId, type: "item" };
      });
    },
    [updateState],
  );

  const confirmDelete = useCallback(() => {
    if (state.deleteInfo.type === "item") {
      deleteTagItem.mutate({
        tagId: state.activeTagId,
        id: state.deleteInfo.id,
      });
    } else {
      deleteTag.mutate(state.deleteInfo.id);
    }
  }, [deleteTag, deleteTagItem, state.deleteInfo, state.activeTagId]);

  const handleOpenAddItems = useCallback(() => {
    updateState((draft) => {
      draft.addItemsDialog.open = true;
    });
  }, [updateState]);

  const handleCloseAddItems = useCallback(() => {
    updateState((draft) => {
      draft.addItemsDialog.open = false;
    });
  }, [updateState]);

  const handleAddItemsSubmit = useCallback(
    (selectedItemIds) => {
      addItems.mutate({
        tagId: state.activeTagId,
        payload: { stock_item_ids: selectedItemIds },
        handleClose: handleCloseAddItems,
      });
    },
    [addItems, state.activeTagId, handleCloseAddItems],
  );

  return {
    state,
    activeTag,
    handleSelectTag,
    handleOpenModal,
    handleCloseModal,
    handleTagNameChange,
    handleSubmit,
    handleDelete,
    handleDeleteItem,
    confirmDelete,
    resetDeleteInfo,
    handleOpenAddItems,
    handleCloseAddItems,
    handleAddItemsSubmit,
    searchHandler: useCallback(
      (e) => {
        updateState((draft) => {
          draft.search = e.target.value;
        });
      },
      [updateState],
    ),
    search: state.search,
    isLoading: listQuery.isLoading,
    isPending: createTag.isPending || updateTag.isPending,
    isDeleting: deleteTag.isPending || deleteTagItem.isPending,
    isAddingItems: addItems.isPending,
  };
};
